import React, { useState, useEffect } from "react";
import { FaCopy, FaDownload, FaRegStopCircle, FaListAlt, FaClock, FaFileContract } from "react-icons/fa";
import { LuSpeech } from "react-icons/lu";
import ReactMarkdown from "react-markdown";
import remarkGFM from "remark-gfm";
import html2pdf from "html2pdf.js";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { toast } from "react-toastify";

const CustomerSummary = ({ show, onClose, summaries, setSummaries, loading, title = "Document Analysis" }) => {
  const [activeTab, setActiveTab] = useState("summary");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const synth = window.speechSynthesis;

  useEffect(() => {
    return () => {
      if (synth.speaking) synth.cancel();
    };
  }, []);

  const parseTimelineData = (timelineString) => {
    if (!timelineString) return null;
    try {
      const cleanedString = timelineString
        .replace(/```json\s*/g, "")
        .replace(/```/g, "")
        .trim();
      const parsed = JSON.parse(cleanedString);
      return Array.isArray(parsed) ? parsed : null;
    } catch (error) {
      console.error("Failed to parse timeline data:", error);
      return null;
    }
  };


  // Helper function to get plain text from content
  const getPlainText = (contentType) => {
    if (!summaries[contentType]) return "";

    if (contentType === "contract_timelines") {
      const timelineData = parseTimelineData(summaries[contentType]);
      if (timelineData) {
        return timelineData.map(item => `${item.date || 'No date'}: ${item.event || 'No event'}`).join("\n");
      }
      return "No timeline data available";
    }

    // For summary and terms, remove markdown formatting
    return summaries[contentType].replace(/[*_#`]+/g, "");
  };

  // Copy to Clipboard Handler
  const handleCopySummary = (summaryType) => {
    if (summaries[summaryType]) {
      const plainText =
        summaryType === "contract_timelines" && Array.isArray(JSON.parse(summaries[summaryType].replace(/```json\n|\n```/g, "")))
          ? JSON.parse(summaries[summaryType].replace(/```json\n|\n```/g, "")).map(item => `${item.date} ${item.event}`).join("\n")
          : summaries[summaryType].replace(/[*_#`]+/g, "");
      navigator.clipboard
        .writeText(plainText)
        .then(() =>
          toast.success(
            `${summaryType.replace("contract_", "").charAt(0).toUpperCase() + summaryType.replace("contract_", "").slice(1)} copied to clipboard!`
          )
        )
        .catch(() => toast.error("Failed to copy content"));
    }
  };

  // Speech Toggle Handler
  const handleSpeechToggle = (contentType) => {
    if (isSpeaking) {
      synth.cancel();
      setIsSpeaking(false);
    } else if (summaries[contentType]) {
      synth.cancel();
      const plainText =
        contentType === "contract_timelines" && Array.isArray(JSON.parse(summaries[contentType].replace(/```json\n|\n```/g, "")))
          ? JSON.parse(summaries[contentType].replace(/```json\n|\n```/g, "")).map(item => `${item.date} ${item.event}`).join("\n")
          : summaries[contentType].replace(/[*_#>]/g, "");

      if (!plainText || plainText === "No timeline data available") {
        toast.error("No content available to read");
        return;
      }

      const chunks = plainText.match(/.{1,200}/g) || [plainText];
      let index = 0;

      const speakChunk = () => {
        if (index >= chunks.length) {
          setIsSpeaking(false);
          synth.cancel();
          return;
        }
        const utterance = new SpeechSynthesisUtterance(chunks[index]);
        utterance.rate = 1;
        utterance.onend = () => {
          index++;
          speakChunk();
        };
        setIsSpeaking(true);
        synth.speak(utterance);
      };
      speakChunk();
    }
  };

  // Stop Speech Handler
  const handleStop = () => {
    if (synth.speaking) {
      synth.cancel();
      setIsSpeaking(false);
    }
  };

  // Download Handler
  const handleDownloadSummary = async (summaryType, format) => {
    if (!summaries[summaryType]) {
      toast.error("No content available to download");
      return;
    }

    try {
      const summaryContent =
        summaryType === "contract_timelines" && Array.isArray(JSON.parse(summaries[summaryType].replace(/```json\n|\n```/g, "")))
          ? JSON.parse(summaries[summaryType].replace(/```json\n|\n```/g, "")).map(item => `${item.date} ${item.event}`).join("\n")
          : summaries[summaryType].replace(/[*_#`]+/g, "");
      const filename = `document_${summaryType.replace("contract_", "")}.${format}`;

      if (format === "pdf") {
        const element = document.createElement("div");
        element.innerHTML = `
          <div>
            <h1 style="font-weight: bold; font-size: 18pt; margin-bottom: 20px; border-bottom: 1px solid #eee; padding-bottom: 10px;">
              ${summaryType.replace("contract_", "").charAt(0).toUpperCase() + summaryType.replace("contract_", "").slice(1)}
            </h1>
            <div style="font-size: 12pt; line-height: 1.6;">
              ${summaryContent.replace(/\n/g, "<br>")}
            </div>
          </div>
        `;
        const opt = {
          margin: 10,
          filename: filename,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
        };
        await html2pdf().from(element).set(opt).save();
      } else if (format === "docx") {
        const doc = new Document({
          sections: [{
            children: [
              new Paragraph({
                text: `${summaryType.replace("contract_", "").charAt(0).toUpperCase() + summaryType.replace("contract_", "").slice(1)}`,
                heading: "Heading1"
              }),
              ...summaryContent.split("\n").map((line) => new Paragraph({
                children: [new TextRun({ text: line })]
              }))
            ],
          }],
        });
        const blob = await Packer.toBlob(doc);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      toast.success(`Content downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download content");
    }
  };

  if (!show) return null;

  return (
    <div className="signature-modal-overlay" onClick={onClose}>
      <div className="signature-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="signature-modal-header">
          <div className="signature-modal-title-box">

            <h3 className="signature-modal-title">{title}</h3>
          </div>
          <button type="button" className="signature-modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="signature-modal-body">
          {loading ? (
            <div className="signature-loader-wrapper">
              <div className="signature-loader"></div>
              <p className="signature-loader-text">Loading summary...</p>
            </div>
          ) : summaries ? (
            <div className="signature-content-wrapper">
              {/* Tab Navigation */}
              <div className="signature-tab-nav">
                <button className={`signature-tab-btn ${activeTab === "summary" ? "signature-tab-active" : ""}`} onClick={() => setActiveTab("summary")}>
                  <FaListAlt className="signature-tab-icon" />
                  <span>Summary</span>
                </button>
                <button className={`signature-tab-btn ${activeTab === "timelines" ? "signature-tab-active" : ""}`} onClick={() => setActiveTab("timelines")}>
                  <FaClock className="signature-tab-icon" />
                  <span>Timelines</span>
                </button>
                <button className={`signature-tab-btn ${activeTab === "terms" ? "signature-tab-active" : ""}`} onClick={() => setActiveTab("terms")}>
                  <FaFileContract className="signature-tab-icon" />
                  <span>Terms</span>
                </button>
              </div>

              {/* Tab Content Area */}
              <div className="signature-tab-content">
                {activeTab === "summary" && (
                  <div className="signature-content-section">
                    <div className="signature-markdown-display">
                      <ReactMarkdown remarkPlugins={[remarkGFM]}>
                        {summaries.contract_summary || "No summary available"}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {activeTab === "timelines" && (
                  <div className="signature-content-section">
                    {summaries.contract_timelines ? (
                      (() => {
                        const timelineData = parseTimelineData(summaries.contract_timelines);
                        return timelineData && timelineData.length > 0 ? (
                          <div className="signature-timeline-wrapper">
                            {timelineData.map((item, index) => (
                              <div key={index} className="signature-timeline-item">
                                <div className="signature-timeline-marker"></div>
                                <div className="signature-timeline-content">
                                  <div className="signature-timeline-date">{item.date || "No date specified"}</div>
                                  <div className="signature-timeline-event">{item.event || "No event details"}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="signature-empty-state">
                            <FaClock className="signature-empty-icon" />
                            <p>No timelines available</p>
                          </div>
                        );
                      })()
                    ) : (
                      <div className="signature-empty-state">
                        <FaClock className="signature-empty-icon" />
                        <p>No timelines available</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "terms" && (
                  <div className="signature-content-section">
                    <div className="signature-markdown-display">
                      <ReactMarkdown remarkPlugins={[remarkGFM]}>
                        {summaries.contract_terms_conditions || "No terms available"}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="signature-empty-state">
              <p>No summary available.</p>
            </div>
          )}
        </div>

        {/* Modal Footer - Action Buttons */}
        <div className="signature-modal-footer">
          <div className="signature-action-group">
            <button className="signature-action-btn signature-btn-copy" onClick={() => handleCopySummary(`contract_${activeTab}`)}>
              <FaCopy />
              <span>Copy</span>
            </button>
            <button className="signature-action-btn signature-btn-download" onClick={() => handleDownloadSummary(`contract_${activeTab}`, "pdf")}>
              <FaDownload />
              <span>PDF</span>
            </button>
            <button className="signature-action-btn signature-btn-speech" onClick={() => (isSpeaking ? handleStop() : handleSpeechToggle(`contract_${activeTab}`))}>
              {isSpeaking ? <FaRegStopCircle /> : <LuSpeech />}
              <span>{isSpeaking ? "Stop" : "Read"}</span>
            </button>
          </div>
          <button type="button" className="signature-btn-close-footer" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default CustomerSummary;
